import type { ScheduleDto } from "@repo/data-store";
import { useDevices, useLocations, useSchedules } from "@repo/data-store";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo } from "react";
import { getActorInfo, ScheduleListItem } from "@/features/scheduling";
import { GroupedList, type GroupedListGroup } from "@/shared/common/grouped-list";
import { practitionerDomainIdFromProfile } from "@/shared/hooks/use-current-practitioner";

export const Route = createFileRoute("/schedule/manage/")({
  component: ScheduleListComponent,
});

type ActorType = "Practitioner" | "Location" | "Device" | "Other";

function ScheduleListComponent() {
  const { profile } = Route.useRouteContext();
  const practitionerId = practitionerDomainIdFromProfile(profile);
  const currentUserRef = practitionerId ? `Practitioner/${practitionerId}` : null;
  const { schedules, isLoading } = useSchedules();
  const { locations: allLocations } = useLocations();
  const { devices: allDevices } = useDevices();

  const groups = useMemo<GroupedListGroup<ScheduleDto>[]>(() => {
    const byType: Record<ActorType, ScheduleDto[]> = {
      Practitioner: [],
      Location: [],
      Device: [],
      Other: [],
    };

    for (const schedule of schedules) {
      const { type } = getActorInfo(schedule);
      byType[type].push(schedule);
    }

    const order: { type: ActorType; label: string }[] = [
      { type: "Practitioner", label: "Practitioners" },
      { type: "Location", label: "Locations" },
      { type: "Device", label: "Devices" },
      { type: "Other", label: "Other" },
    ];

    return order
      .filter((o) => byType[o.type].length > 0)
      .map((o) => ({
        id: o.type,
        name: o.label,
        items: byType[o.type],
      }));
  }, [schedules]);

  return (
    <GroupedList
      groups={groups}
      isLoading={isLoading}
      header={
        <div className="flex items-center justify-between px-4 py-2">
          <h2 className="text-sm font-medium">Schedules</h2>
          <Link
            to="/schedule/manage/new"
            className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-3"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Schedule
          </Link>
        </div>
      }
      renderItem={(schedule) => {
        const { ref } = getActorInfo(schedule);
        const isCurrentUser = ref === currentUserRef;
        return (
          <ScheduleListItem
            schedule={schedule}
            isCurrentUser={isCurrentUser}
            profile={profile}
            allLocations={allLocations}
            allDevices={allDevices}
          />
        );
      }}
      getItemKey={(s) => s.scheduleId}
      emptyMessage="No schedules found"
      hideEmptyGroups
    />
  );
}
